import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  adminImpersonateApi,
  StartImpersonationRequest,
  StopImpersonationRequest,
} from '../../services/api/adminImpersonate';
import { useAuthStore } from '../../stores/authStore';

/**
 * Start a super-admin impersonation session.
 *
 * On success, persists the target org into the auth store and clears all
 * cached queries -- previously-fetched data was scoped to the home org and
 * would otherwise leak across the org boundary in the UI for a few seconds
 * until the next refetch.
 */
export const useStartImpersonation = () => {
  const queryClient = useQueryClient();
  const setImpersonating = useAuthStore(s => s.setImpersonating);

  return useMutation({
    mutationFn: (req: StartImpersonationRequest & { reason: string }) =>
      adminImpersonateApi.start(req).then(target => ({ target, reason: req.reason })),
    onSuccess: ({ target, reason }) => {
      setImpersonating({
        id: target.id,
        name: target.name,
        code: target.code,
        startedAt: Date.now(),
        reason,
      });
      // Wipe caches -- everything was fetched for the home org.
      queryClient.clear();
    },
  });
};

/**
 * End an impersonation session. Tolerant of network failure -- we clear
 * local state first so the user can always escape, then fire-and-forget
 * the audit-log entry.
 */
export const useStopImpersonation = () => {
  const queryClient = useQueryClient();
  const setImpersonating = useAuthStore(s => s.setImpersonating);
  const impersonatingOrg = useAuthStore(s => s.impersonatingOrg);

  return useMutation({
    mutationFn: async (_input?: StopImpersonationRequest | void) => {
      if (!impersonatingOrg) return { status: 'noop' };
      try {
        return await adminImpersonateApi.stop({ org_id: impersonatingOrg.id });
      } catch {
        // The audit-log entry is best-effort. Surfacing the error to the
        // user would prevent them from escaping a bad session.
        return { status: 'best-effort-failed' };
      }
    },
    onSettled: () => {
      setImpersonating(null);
      queryClient.clear();
    },
  });
};
